import * as THREE from 'three'

import { CARRIER } from '../config/constants.js'
import { DEG } from '../util/units.js'

/**
 * Flight deck markings, painted into a canvas.
 *
 * Deck markings carry real information — the landing area edges, the
 * centreline you line up on, where the wires are — so they are drawn from the
 * same constants the physics uses rather than eyeballed. Move a wire in
 * constants.js and the paint moves with it.
 *
 * Canvas mapping (ship frame, feet):
 *   u across  : starboard, PORT_FT on the left to STBD_FT on the right
 *   v down    : forward,   BOW_FT at the top to STERN_FT at the bottom
 */

export const DECK_BOUNDS = {
  PORT_FT: -132,
  STBD_FT: 96,
  STERN_FT: -560,
  BOW_FT: 560,
}

const W = 512
const H = 2048

const spanS = DECK_BOUNDS.STBD_FT - DECK_BOUNDS.PORT_FT
const spanF = DECK_BOUNDS.BOW_FT - DECK_BOUNDS.STERN_FT

const px = (stbdFt) => ((stbdFt - DECK_BOUNDS.PORT_FT) / spanS) * W
const py = (fwdFt) => ((DECK_BOUNDS.BOW_FT - fwdFt) / spanF) * H
const scaleS = W / spanS
const scaleF = H / spanF

/** Deck frame (feet) -> ship frame (feet). */
export function deckToShip(x, y) {
  const c = Math.cos(CARRIER.ANGLE_DEG * DEG)
  const s = Math.sin(CARRIER.ANGLE_DEG * DEG)
  return {
    fwd: CARRIER.RAMP_SHIP_X_FT + x * c + y * s,
    stbd: CARRIER.RAMP_SHIP_Y_FT - x * s + y * c,
  }
}

function moveDeck(ctx, x, y, first) {
  const p = deckToShip(x, y)
  const cx = px(p.stbd)
  const cy = py(p.fwd)
  if (first) ctx.moveTo(cx, cy)
  else ctx.lineTo(cx, cy)
}

function deckLine(ctx, x1, y1, x2, y2) {
  ctx.beginPath()
  moveDeck(ctx, x1, y1, true)
  moveDeck(ctx, x2, y2, false)
  ctx.stroke()
}

/** Text laid along the angled deck, rotated to match it. */
function deckText(ctx, x, y, text, sizeFt, color, rotateExtraDeg = 0) {
  const p = deckToShip(x, y)
  ctx.save()
  ctx.translate(px(p.stbd), py(p.fwd))
  // Deck forward is 9 degrees to port of ship forward; canvas up is ship
  // forward, so the glyphs rotate the same 9 degrees.
  ctx.rotate((-CARRIER.ANGLE_DEG + rotateExtraDeg) * DEG)
  ctx.fillStyle = color
  ctx.font = `700 ${Math.round(sizeFt * scaleF)}px ui-monospace, monospace`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, 0, 0)
  ctx.restore()
}

export function createDeckTexture() {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')

  // ---- base non-skid --------------------------------------------------
  ctx.fillStyle = '#606569'
  ctx.fillRect(0, 0, W, H)

  // Deterministic speckle. A flat grey deck looks like a car park; the noise
  // is what makes it read as non-skid from the groove.
  let seed = 1337
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296
    return seed / 4294967296
  }
  for (let i = 0; i < 26000; i++) {
    const v = rnd()
    ctx.fillStyle = v > 0.5 ? 'rgba(255,255,255,0.030)' : 'rgba(0,0,0,0.045)'
    ctx.fillRect(rnd() * W, rnd() * H, 2, 2)
  }

  // Longitudinal wear streaks from tyres and tractors.
  ctx.strokeStyle = 'rgba(20,20,22,0.10)'
  ctx.lineWidth = 3
  for (let i = 0; i < 30; i++) {
    const s = -120 + rnd() * 200
    ctx.beginPath()
    ctx.moveTo(px(s), py(-520 + rnd() * 200))
    ctx.lineTo(px(s + (rnd() - 0.5) * 12), py(200 + rnd() * 340))
    ctx.stroke()
  }

  const halfW = 40

  // ---- landing area ---------------------------------------------------
  ctx.save()
  ctx.beginPath()
  moveDeck(ctx, -6, -halfW, true)
  moveDeck(ctx, CARRIER.LANDING_AREA_LENGTH_FT, -halfW, false)
  moveDeck(ctx, CARRIER.LANDING_AREA_LENGTH_FT, halfW, false)
  moveDeck(ctx, -6, halfW, false)
  ctx.closePath()
  ctx.fillStyle = 'rgba(30,32,34,0.28)'
  ctx.fill()
  ctx.restore()

  // Landing area edge stripes.
  ctx.strokeStyle = '#e8e8e4'
  ctx.lineWidth = Math.max(2, 1.5 * scaleS)
  deckLine(ctx, -6, -halfW, CARRIER.LANDING_AREA_LENGTH_FT, -halfW)
  deckLine(ctx, -6, halfW, CARRIER.LANDING_AREA_LENGTH_FT, halfW)

  // Centreline: long dashes, the thing you actually line up on.
  ctx.strokeStyle = '#f2f2ee'
  ctx.lineWidth = Math.max(2, 2.0 * scaleS)
  for (let x = 10; x < CARRIER.LANDING_AREA_LENGTH_FT; x += 60) {
    deckLine(ctx, x, 0, x + 34, 0)
  }

  // Ramp threshold bar.
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = Math.max(3, 4 * scaleS)
  deckLine(ctx, 4, -halfW, 4, halfW)

  // ---- cross-deck pendants -------------------------------------------
  CARRIER.WIRES_FT.forEach((wx, i) => {
    // The wire itself.
    ctx.strokeStyle = '#1b1c1e'
    ctx.lineWidth = Math.max(2, 1.6 * scaleS)
    deckLine(ctx, wx, -halfW - 6, wx, halfW + 6)

    // Wire number painted outboard, port side.
    deckText(ctx, wx, -halfW - 16, String(i + 1), 16, '#dcdcd6')
  })

  // Touchdown target box around the 3 wire.
  const target = CARRIER.WIRES_FT[CARRIER.TARGET_WIRE - 1]
  ctx.strokeStyle = 'rgba(255,214,102,0.85)'
  ctx.lineWidth = Math.max(2, 1.2 * scaleS)
  ctx.setLineDash([6, 6])
  ctx.beginPath()
  moveDeck(ctx, target - 20, -20, true)
  moveDeck(ctx, target + 20, -20, false)
  moveDeck(ctx, target + 20, 20, false)
  moveDeck(ctx, target - 20, 20, false)
  ctx.closePath()
  ctx.stroke()
  ctx.setLineDash([])

  // ---- foul lines -----------------------------------------------------
  ctx.strokeStyle = '#c8332b'
  ctx.lineWidth = Math.max(2, 1.4 * scaleS)
  ctx.setLineDash([14, 10])
  deckLine(ctx, -10, halfW + 14, CARRIER.LANDING_AREA_LENGTH_FT, halfW + 14)
  deckLine(ctx, -10, -halfW - 14, CARRIER.LANDING_AREA_LENGTH_FT, -halfW - 14)
  ctx.setLineDash([])

  // ---- catapults ------------------------------------------------------
  ctx.strokeStyle = 'rgba(230,230,225,0.55)'
  ctx.lineWidth = Math.max(2, 1.2 * scaleS)

  // Bow cats 1 and 2, parallel to the ship's centreline.
  for (const s of [-18, 26]) {
    ctx.beginPath()
    ctx.moveTo(px(s), py(60))
    ctx.lineTo(px(s), py(520))
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(px(s - 9), py(60))
    ctx.lineTo(px(s - 9), py(520))
    ctx.stroke()
  }

  // Waist cats 3 and 4, angled with the landing area.
  ctx.strokeStyle = 'rgba(230,230,225,0.40)'
  for (const y of [-72, -100]) {
    deckLine(ctx, 230, y, 640, y)
  }

  // ---- deck edge outline ----------------------------------------------
  ctx.strokeStyle = 'rgba(240,240,236,0.35)'
  ctx.lineWidth = 3
  ctx.strokeRect(1.5, 1.5, W - 3, H - 3)

  // ---- elevators ------------------------------------------------------
  ctx.strokeStyle = 'rgba(220,220,215,0.5)'
  ctx.lineWidth = Math.max(2, 1.0 * scaleS)
  const elevators = [
    { f: 330, s: 60, len: 85, wid: 34 },
    { f: 90, s: 60, len: 85, wid: 34 },
    { f: -300, s: 60, len: 85, wid: 34 },
    { f: 250, s: -96, len: 80, wid: 30 },
  ]
  for (const e of elevators) {
    ctx.strokeRect(
      px(e.s - e.wid / 2),
      py(e.f + e.len / 2),
      e.wid * scaleS,
      e.len * scaleF,
    )
  }

  // ---- hull number on the bow ----------------------------------------
  ctx.save()
  ctx.translate(px(0), py(470))
  ctx.fillStyle = 'rgba(238,238,232,0.9)'
  ctx.font = `700 ${Math.round(52 * scaleF)}px ui-monospace, monospace`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('73', 0, 0)
  ctx.restore()

  // ---- landing area hull number, painted along the angled deck --------
  deckText(ctx, 470, 0, '73', 46, 'rgba(238,238,232,0.85)')

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 8
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping

  // ExtrudeGeometry's default UV generator writes the shape's own coordinates
  // into uv, so map those feet onto 0..1 here.
  texture.repeat.set(1 / spanS, 1 / spanF)
  texture.offset.set(-DECK_BOUNDS.PORT_FT / spanS, -DECK_BOUNDS.STERN_FT / spanF)

  return texture
}
